import type { TranslationParams } from '../i18n'
import { formatQuantity, parseQuantity, type UnitSystem } from '../utils/units'

const MIN_HEIGHT_CM = 80
const MAX_HEIGHT_CM = 250
const MIN_TARGET_KG = 20
const MAX_TARGET_KG = 400
/** Sanity cap for the monthly run-effort target (≈ 500 km/month at marathon pace). */
const MAX_MONTHLY_RUN_EFFORT = 10000

type SyncKeyOptions = {
   loadRemote?: boolean
   pushLocal?: boolean
   action?: string
}

type SyncKeyResult = {
   ok: boolean
   remoteSynced: boolean
   remoteEnabled: boolean
}

type AccountFormInput = {
   heightInput?: string
   targetWeightInput?: string
   targetRunEffortInput?: string
}

type UserProfileState = {
   heightCm: number | null
   targetWeightKg: number | null
   targetMonthlyRunEffort: number | null
   unitSystem: UnitSystem
}

type AccountControllerDeps = {
   appShareBaseUrl: string
   buildUserDataExport: () => unknown
   canUseRemoteSync: () => boolean
   closeQrOverlay: () => void
   getAccountFormState: () => {
      heightInput: string
      targetWeightInput: string
      targetRunEffortInput: string
   }
   getCurrentFast: () => unknown
   getFastHistoryLength: () => number
   getSyncServerUpdatedAt: () => unknown
   getUserId: () => string
   getUserProfile: () => UserProfileState
   getWeightHistoryLength: () => number
   markStateChanged: () => void
   openQrOverlay: (options: { title: string; description: string; url: string }) => void
   parseTimestampToNumber: (value: unknown) => number | null
   removeLocalAccountStorage: () => void
   resetLocalState: () => void
   saveIdentity: () => void
   saveState: () => void
   setFastCompletionNotificationInFlight: (value: boolean) => void
   setSyncKeyState: (newKey: string, options?: SyncKeyOptions) => Promise<SyncKeyResult>
   setUserId: (value: string) => void
   syncAccountStateToSolid: (overrides?: {
      statusMessage?: string
      downloadStatusMessage?: string
      isDownloading?: boolean
   }) => void
   syncState: (action: string) => Promise<unknown>
   syncWeightStateToSolid: () => void
   translate: (key: string, params?: TranslationParams) => string
   triggerJsonDownload: (filename: string, payload: unknown) => void
   userProfile: UserProfileState
}

/**
 * Returns whether two heights are effectively equal (within 0.05 cm).
 *
 * @param a - The first height, or null.
 * @param b - The second height, or null.
 * @returns True when equal.
 */
function heightsEqual(a: number | null, b: number | null) {
   if (typeof a !== 'number' || typeof b !== 'number') {
      return a === b
   }
   return Math.abs(a - b) < 0.05
}

/**
 * Parses the monthly run-effort target input: empty clears the target, otherwise
 * it must be a positive number no larger than the cap.
 *
 * @param raw - The trimmed input value.
 * @returns The target (null when cleared), or `ok: false` when invalid.
 */
function parseRunEffortTarget(
   raw: string,
): { ok: true; value: number | null } | { ok: false } {
   if (raw === '') {
      return { ok: true, value: null }
   }
   const parsed = Number(raw)
   if (!Number.isFinite(parsed) || parsed <= 0 || parsed > MAX_MONTHLY_RUN_EFFORT) {
      return { ok: false }
   }
   return { ok: true, value: Number(parsed.toFixed(1)) }
}

/**
 * Generates a random anonymous sync key (128-bit hex).
 *
 * @returns The new sync key.
 * @throws Error when secure randomness is unavailable.
 */
function generateAnonymousSyncKey() {
   if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16)
      window.crypto.getRandomValues(bytes)
      return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
   }

   throw new Error('Secure random number generation is unavailable.')
}

/**
 * Creates a new anonymous sync key (confirming replacement) and uploads local data.
 * @param deps - The controller dependencies.
 */
async function handleCreateSyncKey(deps: AccountControllerDeps) {
   const existingKey = deps.getUserId()
   if (existingKey) {
      const confirmReplace = window.confirm(
         'Creating a new synchronization key will replace the current one. Continue?',
      )
      if (!confirmReplace) {
         return
      }
   }

   const newKey = generateAnonymousSyncKey()
   const remoteEnabled = deps.canUseRemoteSync()

   deps.syncAccountStateToSolid({
      statusMessage:
         remoteEnabled ?
            'Generating new sync key and uploading your data...'
         :  'New sync key created locally. Connect over HTTP(S) to sync with the cloud.',
   })

   deps.closeQrOverlay()

   const result = await deps.setSyncKeyState(newKey, {
      pushLocal: true,
      action: 'create-sync-key',
   })

   if (!result.ok) {
      deps.syncAccountStateToSolid({
         statusMessage:
            'Sync key created, but uploading data failed. Try again when you have a stable connection.',
      })
      return
   }

   deps.syncAccountStateToSolid({
      statusMessage:
         result.remoteSynced ?
            'New sync key created. Your current data is linked to this key.'
         :  'New sync key created locally. Connect over HTTP(S) to sync with the cloud.',
   })
}

/**
 * Switches to an existing sync key, resetting local state and loading its cloud data.
 *
 * @param deps - The controller dependencies.
 * @param trimmed - The entered sync key.
 */
async function handleEnterSyncKey(deps: AccountControllerDeps, trimmed: string) {
   if (!trimmed) {
      return
   }

   if (trimmed === deps.getUserId()) {
      deps.syncAccountStateToSolid({
         statusMessage: 'This synchronization key is already active.',
      })
      return
   }

   deps.resetLocalState()
   deps.closeQrOverlay()

   const remoteEnabled = deps.canUseRemoteSync()
   if (remoteEnabled) {
      deps.syncAccountStateToSolid({
         statusMessage: 'Sync key saved. Loading data linked to this key...',
      })
   }

   const result = await deps.setSyncKeyState(trimmed, {
      loadRemote: remoteEnabled,
      pushLocal: false,
   })

   if (!remoteEnabled) {
      deps.syncAccountStateToSolid({
         statusMessage:
            'Sync key saved locally. Connect over HTTP(S) to download cloud data.',
      })
      return
   }

   deps.syncAccountStateToSolid({
      statusMessage:
         result.ok ?
            'Cloud data loaded for the provided synchronization key.'
         :  'Sync key saved, but loading cloud data failed. Try again later.',
   })
}

/**
 * Stops syncing on this device by forgetting the sync key. Local data stays on the
 * device and the cloud copy stays under the key, so entering it again reconnects.
 *
 * @param deps - The controller dependencies.
 */
function handleStopSync(deps: AccountControllerDeps) {
   if (!deps.getUserId()) {
      return
   }
   deps.closeQrOverlay()
   deps.setUserId('')
   deps.saveIdentity()
   deps.syncAccountStateToSolid({
      statusMessage: deps.translate('Sync stopped. Your data stays on this device.'),
   })
}

/**
 * Opens a QR overlay for sharing the active sync key with another device.
 * @param deps - The controller dependencies.
 */
function handleCreateQrCode(deps: AccountControllerDeps) {
   const userId = deps.getUserId()
   if (!userId) {
      window.alert('Create or enter a synchronization key before generating a QR code.')
      return
   }

   const serverTimestamp = deps.parseTimestampToNumber(deps.getSyncServerUpdatedAt())
   if (!serverTimestamp) {
      window.alert('Sync your data first before generating a QR code.')
      return
   }

   const shareUrl = `${deps.appShareBaseUrl}?userId=${encodeURIComponent(userId)}`
   deps.openQrOverlay({
      title: deps.translate('Sync this key on another device'),
      description: deps.translate(
         'Scan to load this synchronization key on another device or open the link below.',
      ),
      url: shareUrl,
   })
}

/**
 * Deletes all account data locally and, when possible, from the cloud after confirmation.
 *
 * @param deps - The controller dependencies.
 * @param debugStateStorageKey - The localStorage key holding sync debug state.
 */
async function handleAccountDelete(
   deps: AccountControllerDeps,
   debugStateStorageKey: string,
) {
   const confirmation = window.confirm(
      'Deleting your account removes all fasts, weight entries, targets, and your synchronization key from this device and the cloud backup. This action cannot be undone. Delete all account data?',
   )
   if (!confirmation) {
      return
   }

   const previousUserId = deps.getUserId()
   const remoteEnabled = previousUserId ? deps.canUseRemoteSync() : false

   deps.resetLocalState()
   deps.setFastCompletionNotificationInFlight(false)

   deps.removeLocalAccountStorage()
   localStorage.removeItem(debugStateStorageKey)

   let remoteDeletionSucceeded = !previousUserId
   if (previousUserId && remoteEnabled) {
      try {
         const result = await deps.syncState('delete-account')
         remoteDeletionSucceeded = result !== false
      } catch (error) {
         remoteDeletionSucceeded = false
         console.error('Failed to delete remote account data', error)
      }
   } else if (previousUserId) {
      remoteDeletionSucceeded = false
   }

   deps.setUserId('')
   deps.saveIdentity()
   deps.syncAccountStateToSolid()
   let statusMessage =
      'All account data deleted locally and from the sync service. Create a new synchronization key to start fresh.'
   if (previousUserId && !remoteEnabled) {
      statusMessage =
         'Local data deleted. Serve the app over HTTPS to remove synced data, then delete again.'
   } else if (previousUserId && !remoteDeletionSucceeded) {
      statusMessage =
         'Local data deleted, but the sync service could not be reached. Try again when you have an internet connection.'
   }
   deps.syncAccountStateToSolid({ statusMessage })
}

/**
 * Exports all account data to a downloaded JSON file.
 * @param deps - The controller dependencies.
 */
function handleAccountDownload(deps: AccountControllerDeps) {
   const userId = deps.getUserId()
   const profile = deps.getUserProfile()
   const hasData =
      Boolean(deps.getCurrentFast()) ||
      deps.getFastHistoryLength() > 0 ||
      deps.getWeightHistoryLength() > 0 ||
      typeof profile.heightCm === 'number' ||
      typeof profile.targetWeightKg === 'number' ||
      Boolean(userId)

   if (!hasData) {
      return
   }

   deps.syncAccountStateToSolid({
      downloadStatusMessage: deps.translate('Preparing your data export…'),
      isDownloading: true,
   })

   window.setTimeout(() => {
      try {
         const payload = deps.buildUserDataExport()
         const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
         deps.triggerJsonDownload(`fasting-tracker-export-${timestamp}.json`, payload)
         deps.syncAccountStateToSolid({
            downloadStatusMessage: deps.translate(
               'Data export ready. Check your downloads.',
            ),
         })
      } catch (error) {
         console.error('Failed to export account data', error)
         deps.syncAccountStateToSolid({
            downloadStatusMessage: deps.translate(
               'Data export failed. Please try again.',
            ),
         })
      } finally {
         deps.syncAccountStateToSolid({
            isDownloading: false,
         })
      }
   }, 0)
}

type ProfileFieldResult<T> =
   { ok: true; value: T | null; used: boolean } | { ok: false; message: string }

/**
 * Parses the height field: empty clears a stored height, otherwise the value
 * (typed in the active unit system) must fall within the sane range.
 *
 * @param raw - The trimmed input.
 * @param profile - The current profile.
 * @param translate - The translation function.
 * @returns The parsed height in cm, or an error message.
 */
function parseHeightField(
   raw: string,
   profile: UserProfileState,
   translate: AccountControllerDeps['translate'],
): ProfileFieldResult<number> {
   if (raw === '') {
      const used = typeof profile.heightCm === 'number'
      return { ok: true, value: used ? null : profile.heightCm, used }
   }
   const heightCm = parseQuantity('length', raw, profile.unitSystem)
   if (heightCm === null || heightCm < MIN_HEIGHT_CM || heightCm > MAX_HEIGHT_CM) {
      return {
         ok: false,
         message: translate('Height must be between {min} and {max}.', {
            min: formatQuantity('length', MIN_HEIGHT_CM, profile.unitSystem, {
               digits: 0,
            }),
            max: formatQuantity('length', MAX_HEIGHT_CM, profile.unitSystem, {
               digits: 0,
            }),
         }),
      }
   }
   return { ok: true, value: Number(heightCm.toFixed(1)), used: true }
}

/**
 * Parses the target-weight field: empty clears a stored target, otherwise the
 * value (typed in the active unit system) must fall within the sane range.
 *
 * @param raw - The trimmed input.
 * @param profile - The current profile.
 * @param translate - The translation function.
 * @returns The parsed target in kg, or an error message.
 */
function parseTargetWeightField(
   raw: string,
   profile: UserProfileState,
   translate: AccountControllerDeps['translate'],
): ProfileFieldResult<number> {
   if (raw === '') {
      const used = typeof profile.targetWeightKg === 'number'
      return { ok: true, value: used ? null : profile.targetWeightKg, used }
   }
   const targetKg = parseQuantity('mass', raw, profile.unitSystem)
   if (targetKg === null || targetKg < MIN_TARGET_KG || targetKg > MAX_TARGET_KG) {
      return {
         ok: false,
         message: translate('Target weight must be between {min} and {max}.', {
            min: formatQuantity('mass', MIN_TARGET_KG, profile.unitSystem, { digits: 0 }),
            max: formatQuantity('mass', MAX_TARGET_KG, profile.unitSystem, { digits: 0 }),
         }),
      }
   }
   return { ok: true, value: Number(targetKg.toFixed(1)), used: true }
}

/**
 * Returns whether two target weights differ meaningfully (≥ 0.05 kg).
 *
 * @param previous - The stored target, or null.
 * @param next - The new target, or null.
 * @returns True when changed.
 */
function targetWeightChanged(previous: number | null, next: number | null) {
   if (typeof previous === 'number' && typeof next === 'number') {
      return Math.abs(previous - next) >= 0.05
   }
   return previous !== next
}

/**
 * Applies parsed profile fields to the profile, marking state changed for each
 * real difference.
 *
 * @param deps - The controller dependencies.
 * @param height - The parsed height field.
 * @param targetWeight - The parsed target-weight field.
 * @param runEffort - The parsed run-effort target.
 * @returns True when anything changed.
 */
function applyProfileChanges(
   deps: AccountControllerDeps,
   height: Extract<ProfileFieldResult<number>, { ok: true }>,
   targetWeight: Extract<ProfileFieldResult<number>, { ok: true }>,
   runEffort: number | null,
) {
   const profile = deps.userProfile
   let anyChange = false
   if (runEffort !== profile.targetMonthlyRunEffort) {
      profile.targetMonthlyRunEffort = runEffort
      anyChange = true
   }
   if (height.used) {
      anyChange = !heightsEqual(height.value, profile.heightCm) || anyChange
      profile.heightCm = height.value
   }
   if (targetWeight.used) {
      anyChange =
         targetWeightChanged(profile.targetWeightKg, targetWeight.value) || anyChange
      profile.targetWeightKg = targetWeight.value
   }
   if (anyChange) {
      deps.markStateChanged()
   }
   return anyChange
}

/**
 * Reports the outcome of a profile save, syncing to the cloud when possible.
 *
 * @param deps - The controller dependencies.
 * @param anyChange - Whether the save changed anything.
 */
async function reportProfileSaved(deps: AccountControllerDeps, anyChange: boolean) {
   if (!anyChange) {
      deps.syncAccountStateToSolid({ statusMessage: 'No profile changes to save.' })
      return
   }
   const hasSyncKey = Boolean(deps.getUserId())
   if (hasSyncKey && deps.canUseRemoteSync()) {
      deps.syncAccountStateToSolid({ statusMessage: 'Profile saved. Syncing changes...' })
      const syncResponse = await deps.syncState('update-profile')
      deps.syncAccountStateToSolid({
         statusMessage:
            syncResponse === false ?
               'Profile saved locally, but syncing failed. Try again later.'
            :  'Profile saved and synced.',
      })
      return
   }
   deps.syncAccountStateToSolid({
      statusMessage:
         hasSyncKey ?
            'Profile saved locally. Connect over HTTP(S) to sync these changes.'
         :  'Profile saved locally. Create a synchronization key to back up your data.',
   })
}

/**
 * Validates and saves profile height and targets, syncing changes when possible.
 *
 * @param deps - The controller dependencies.
 * @param formData - Optional form values; falls back to the current input state.
 */
async function handleAccountFormSubmit(
   deps: AccountControllerDeps,
   formData: AccountFormInput = {},
) {
   const formState = deps.getAccountFormState()
   const raw = (value: string | undefined, fallback: string) =>
      (typeof value === 'string' ? value : fallback).trim()
   const profile = deps.userProfile
   const height = parseHeightField(
      raw(formData.heightInput, formState.heightInput),
      profile,
      deps.translate,
   )
   const targetWeight = parseTargetWeightField(
      raw(formData.targetWeightInput, formState.targetWeightInput),
      profile,
      deps.translate,
   )
   const effortTarget = parseRunEffortTarget(
      raw(formData.targetRunEffortInput, formState.targetRunEffortInput),
   )
   if (!height.ok) {
      deps.syncAccountStateToSolid({ statusMessage: height.message })
      return
   }
   if (!targetWeight.ok) {
      deps.syncAccountStateToSolid({ statusMessage: targetWeight.message })
      return
   }
   if (!effortTarget.ok) {
      deps.syncAccountStateToSolid({
         statusMessage: deps.translate(
            'Monthly run effort target must be a positive number up to {max}.',
            { max: MAX_MONTHLY_RUN_EFFORT },
         ),
      })
      return
   }

   const anyChange = applyProfileChanges(deps, height, targetWeight, effortTarget.value)
   deps.saveState()
   deps.syncAccountStateToSolid()
   deps.syncWeightStateToSolid()
   await reportProfileSaved(deps, anyChange)
}

/**
 * Creates the account controller handling sync-key creation/entry/stop, QR sharing, deletion, export, and profile edits.
 *
 * @param deps - Injected state accessors and side-effect callbacks.
 * @returns The account action handlers.
 */
export function createAccountController(deps: AccountControllerDeps) {
   return {
      handleAccountDelete: (debugStateStorageKey: string) =>
         handleAccountDelete(deps, debugStateStorageKey),
      handleAccountDownload: () => handleAccountDownload(deps),
      handleAccountFormSubmit: (formData?: AccountFormInput) =>
         handleAccountFormSubmit(deps, formData),
      handleCreateQrCode: () => handleCreateQrCode(deps),
      handleCreateSyncKey: () => handleCreateSyncKey(deps),
      handleEnterSyncKey: (trimmed: string) => handleEnterSyncKey(deps, trimmed),
      handleStopSync: () => handleStopSync(deps),
   }
}
